/** @jsxImportSource preact */
import * as m from "../paraglide/messages.js";
import {
  CloseIcon,
  FullscreenIcon,
  MinimizeIcon,
  RestartIcon,
  SpaceToggleIcon,
} from "./icons";
import { headerButtonStyle } from "./launcher-capsule";
import { SERIF_META_FONT, type WidgetTheme } from "./theme";

export const PANEL_HEADER_STYLES = `
/* 32px 可见按钮保留 44px 点击区；平时不垫底色，悬停才有。 */
.gn-header-actions > button { position:relative; isolation:isolate; }
.gn-header-actions > button::before { content:""; position:absolute; inset:6px; z-index:-1; border-radius:inherit; }
.gn-header-actions > button:hover::before { background:var(--gn-header-action-bg); }
.gn-header-actions > button:active::before { opacity:.55; }
.gn-header-actions > button:focus-visible { outline:none; }
.gn-header-actions > button:focus-visible::before { outline:2px solid currentColor; outline-offset:2px; }
.gn-header-dot::before { content:"·"; margin:0 .35em; }
.gn-header-status { display:inline-block; }
.gn-header-status::first-letter { text-transform:lowercase; }
/* 空间开着时的两个圆按钮：最小化、展开到空间或收回聊天窗。手机 44，PC 40。 */
.gn-header-round { display:grid; place-items:center; flex:none; width:40px; height:40px; padding:0; border:0; border-radius:50%; background:var(--gn-header-action-bg); color:var(--gn-header-round-ink); cursor:pointer; transition:filter .15s; }
.gn-header-round[data-drawer] { width:44px; height:44px; }
.gn-header-round:hover { filter:brightness(.96); }
.gn-header-round:active { filter:brightness(.92); }
.gn-header-round:focus-visible { outline:2px solid currentColor; outline-offset:2px; }
`;

const ellipsis = {
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

/** 带 `/` 或 `.` 的当图片地址，其余当字母头像。 */
function isImageAvatar(avatar: string): boolean {
  return /[/.]/.test(avatar);
}

export interface PanelHeaderProps {
  theme: WidgetTheme;
  /** 商家圆角：标题栏按钮取 min(r, 10)。 */
  radius: number;
  title: string;
  /** 空字符串不画状态行。 */
  subtitle: string;
  /** 空值不画；不含 `/`、`.` 的短字符串按字母头像渲染，否则当图片地址。 */
  avatar: string;
  /** 在移动抽屉里：标题栏也是拖动抓手。 */
  drawer?: boolean;
  fullscreen?: boolean;
  /** 给缩放手柄留出的那一侧。 */
  reserveSide?: "left" | "right";
  onClear?: () => void;
  onClose?: () => void;
  onToggleFullscreen?: () => void;
  /**
   * 商家开着 Your space 时，右上角换成两个圆按钮：最小化、展开到空间（enter）或收回聊天窗（exit）。
   * 全屏和关闭不再画：最小化就是原来的关闭。
   */
  space?: "enter" | "exit";
  onToggleSpace?: () => void;
}

/** 聊天窗标题栏：店铺 Logo、名字和状态行，右侧重开、全屏、关闭。标题栏归商家，不放角色。 */
export function PanelHeader({
  theme,
  radius,
  title,
  subtitle,
  avatar,
  drawer = false,
  fullscreen = false,
  reserveSide,
  onClear,
  onClose,
  onToggleFullscreen,
  space,
  onToggleSpace,
}: PanelHeaderProps) {
  const hasImageAvatar = avatar !== "" && isImageAvatar(avatar);
  const hasMonogram = avatar !== "" && !hasImageAvatar;
  // 标题栏按钮只占 32px 的位置，44px 点击区用负 margin 向外借。
  const headerActionStyle = {
    ...headerButtonStyle,
    borderRadius: Math.min(radius, 10),
    margin: -6,
  } as const;
  const headerPaddingY = theme.editorial ? 14 : 12;
  const metaFont = theme.serif ? SERIF_META_FONT : undefined;
  return (
    <header
      style={{
        touchAction: drawer ? "none" : undefined,
        userSelect: drawer ? "none" : undefined,
        alignItems: "center",
        borderBottom: `1px solid ${theme.divider}`,
        color: theme.ink,
        display: "flex",
        flex: "none",
        gap: theme.serif ? 12 : 10,
        boxSizing: "border-box",
        paddingTop: fullscreen
          ? `calc(${String(headerPaddingY)}px + env(safe-area-inset-top, 0px))`
          : headerPaddingY,
        paddingBottom: headerPaddingY,
        paddingLeft: reserveSide === "left" ? 30 : theme.editorial ? 18 : 16,
        paddingRight: reserveSide === "right" ? 30 : 12,
      }}
    >
      {hasImageAvatar ? (
        // 别裁成圆形：横版字标会被裁得只剩中间几个字母。
        <img
          alt=""
          src={avatar}
          style={{
            display: "block",
            flex: "none",
            height: 28,
            maxWidth: 140,
            objectFit: "contain",
            objectPosition: "left center",
            width: "auto",
          }}
        />
      ) : (
        hasMonogram && (
          <div
            style={{
              background: theme.accentFill,
              borderRadius: "50%",
              color: theme.accentFillFg,
              display: "grid",
              flex: "none",
              fontSize: 13,
              fontWeight: 600,
              height: 34,
              letterSpacing: ".02em",
              placeItems: "center",
              width: 34,
            }}
          >
            {avatar}
          </div>
        )
      )}
      {theme.editorial ? (
        <div
          style={{
            ...ellipsis,
            color: theme.muted,
            fontFamily: metaFont,
            fontSize: theme.serif ? 11 : 12,
          }}
        >
          {title}
          {subtitle && (
            <>
              <span class="gn-header-dot" aria-hidden="true" />
              <span class="gn-header-status">{subtitle}</span>
            </>
          )}
        </div>
      ) : (
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              ...ellipsis,
              fontSize: theme.serif ? 15 : 14,
              fontWeight: theme.serif ? 400 : 500,
            }}
          >
            {title}
          </div>
          {subtitle && (
            <div
              style={{
                ...ellipsis,
                color: theme.muted,
                fontFamily: metaFont,
                fontSize: 11,
              }}
            >
              {subtitle}
            </div>
          )}
        </div>
      )}
      <div
        class="gn-header-actions"
        data-base-ui-swipe-ignore={drawer ? "" : undefined}
        style={{
          alignItems: "center",
          color: theme.iconColor,
          display: "flex",
          flex: "none",
          gap: 2,
          marginLeft: "auto",
          "--gn-header-action-bg": theme.subtle,
          "--gn-header-round-ink": theme.ink,
        }}
      >
        {onClear && (
          <button
            aria-label={m.widget_header_clear()}
            onClick={onClear}
            style={headerActionStyle}
            type="button"
          >
            <RestartIcon size={16} />
          </button>
        )}
        {space !== undefined && (
          <div style={{ display: "flex", gap: 8, marginLeft: 6 }}>
            <button
              aria-label={m.widget_header_minimize()}
              class="gn-header-round"
              data-drawer={drawer || undefined}
              onClick={onClose}
              type="button"
            >
              <MinimizeIcon size={18} />
            </button>
            <button
              aria-label={
                space === "exit"
                  ? m.widget_header_exit_space()
                  : m.widget_header_enter_space()
              }
              class="gn-header-round"
              data-drawer={drawer || undefined}
              onClick={onToggleSpace}
              type="button"
            >
              <SpaceToggleIcon open={space === "exit"} size={18} />
            </button>
          </div>
        )}
        {space === undefined && onToggleFullscreen && (
          <button
            aria-label={
              fullscreen
                ? m.widget_header_exit_fullscreen()
                : m.widget_header_enter_fullscreen()
            }
            onClick={onToggleFullscreen}
            style={headerActionStyle}
            type="button"
          >
            <FullscreenIcon size={16} active={fullscreen} />
          </button>
        )}
        {space === undefined && onClose && (
          <button
            aria-label={m.widget_header_close()}
            onClick={onClose}
            style={headerActionStyle}
            type="button"
          >
            <CloseIcon size={14} />
          </button>
        )}
      </div>
    </header>
  );
}
